/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { Alert } from "reactstrap";

import { useConnectivity } from "../../../offline/connectivityMonitor";
import {
    onReplayFailure,
    onReplayProgress,
    STEP_COUNT,
} from "../../../offline/replayEngine";
import { getActiveSessionId } from "../../../offline/sessionManager";

/**
 * Status-aware offline banner. State table mirrors design §5.8.6.
 *
 *   | State                      | Display                                               |
 *   | online, no pending         | (hidden)                                              |
 *   | offline, no session        | "You are offline."                                    |
 *   | offline, collecting        | "You are offline. Changes are saved locally."         |
 *   | offline, ready_to_sync > 0 | "You are offline. N check(s) waiting to sync."        |
 *   | online, syncing            | "Syncing check... (step X/Y)"                         |
 *   | online, sync failed        | "Sync failed: [reason]"                               |
 *   | storage near full          | "Device storage is almost full. ..."  [Phase 5.8]     |
 *
 * The connectivity data (`isOnline`, `pendingSessions`) comes from
 * <ConnectivityProvider> at App level, which we mount outside
 * <ConfigProvider> deliberately so this banner works for tenants
 * regardless of the offline feature flag.
 *
 * The two-step modal for tenant-switch invalidation (issue 3.3) is a
 * separate component and replaces this banner during its lifecycle.
 */
export default function IsOfflinebar() {
    const { isOnline, pendingSessions } = useConnectivity();
    const activeSessionId = getActiveSessionId();

    // Phase 6.3: subscribe to the replay engine's progress + failure
    // streams so the banner can show "Syncing check... (step X/Y)" and
    // sync-failure messages.
    const [progress, setProgress] = useState(null);
    const [failure, setFailure] = useState(null);
    useEffect(() => {
        const unsubProgress = onReplayProgress((p) => {
            setProgress(p);
            if (p) setFailure(null);
        });
        const unsubFailure = onReplayFailure((f) => setFailure(f));
        return () => {
            unsubProgress();
            unsubFailure();
        };
    }, []);

    // Online + replay in flight: show step progress.
    if (isOnline && progress) {
        return (
            <Alert color="info" className="text-center py-1 fs-7 mb-0 rounded-0">
                Syncing check... (step {progress.step + 1}/{STEP_COUNT})
            </Alert>
        );
    }

    // Online + recent failure: surface the reason. Stays until a new
    // progress event clears it (next replay run resets failure to null
    // via the progress subscription above).
    if (isOnline && failure) {
        return (
            <Alert color="danger" className="text-center py-1 fs-7 mb-0 rounded-0">
                Sync failed: <strong>{failure.reason}</strong>
            </Alert>
        );
    }

    // Online, no pending, no syncing — hidden.
    if (isOnline && pendingSessions === 0) {
        return null;
    }

    let message;
    if (!isOnline) {
        if (activeSessionId) {
            message = (
                <span>
                    You are <strong>offline</strong>. Changes are saved locally.
                </span>
            );
        } else if (pendingSessions > 0) {
            message = (
                <span>
                    You are <strong>offline</strong>. {pendingSessions} check{pendingSessions === 1 ? "" : "s"} waiting to sync.
                </span>
            );
        } else {
            message = (
                <span>
                    You are <strong>offline</strong>.
                </span>
            );
        }
    } else {
        // Online with pendingSessions > 0 but no active replay (between
        // a transition and the engine actually starting a step). Brief
        // window in practice.
        message = (
            <span>
                {pendingSessions} offline check{pendingSessions === 1 ? "" : "s"} waiting to sync.
            </span>
        );
    }

    const color = !isOnline ? "danger" : "info";
    return (
        <Alert color={color} className="text-center py-1 fs-7 mb-0 rounded-0">
            {message}
        </Alert>
    );
}
